import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Modal, Button, Icon, Loader, Checkbox } from "semantic-ui-react";
import { isEmpty, orderBy } from "lodash";
import { useReactToPrint } from "react-to-print";

import dataHandler from "@Utils/dataHandler";
import { getChatRemainingMessages } from "@Redux/Actions/chats";

import styles from "./styles.module.css";

const HistoricChatModal = ({ dispatch, open, onClose, chatData, companyName, userName, variant }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [messages, setMessages] = useState([]);
	const [showAutogestion, setShowAutogestion] = useState(false);
	const printRef = useRef(null);

	const handlePrint = useReactToPrint({
		content: () => {
			return printRef.current;
		},
	});

	const fetchMessages = useCallback(async () => {
		if (!open || !chatData) {
			setMessages([]);
			return;
		}

		setIsLoading(true);
		try {
			const chatId = chatData.chatId || chatData.id;
			const chatResponse = await dataHandler({
				dispatch,
				method: "GET",
				url: `/chatsh/${chatId}`,
			});

			const chat = chatResponse?.chat || chatResponse;
			let fetchedMessages = chat?.messages || [];

			const remaining = await dispatch(
				getChatRemainingMessages({
					channelCid: chat?.channelCid || chatData?.channelCid || "",
					chatId,
					offset: 0,
					isLoading: () => {},
					isHistoric: true,
				})
			);
			if (remaining && Array.isArray(remaining)) {
				const filteredRemaining = remaining.filter((msg) => {
					return msg.chatId === chatId;
				});
				fetchedMessages = [...fetchedMessages, ...filteredRemaining];
			}

			// Filter to only include messages from this specific conversation
			const conversationMessages = fetchedMessages.filter((msg) => {
				return msg.chatId === chatId;
			});

			// Deduplicate by message id or timestamp
			const uniqueMessages = Array.from(
				new Map(
					conversationMessages.map((item) => {
						return [item.id || item.timestamp, item];
					})
				).values()
			);

			// Ensure messages are sorted by date
			const sortedMessages = orderBy(uniqueMessages, ["timestamp"], ["asc"]);
			setMessages(sortedMessages);
		} catch (error) {
			setMessages([]);
		} finally {
			setIsLoading(false);
		}
	}, [open, chatData, dispatch]);

	useEffect(() => {
		fetchMessages();
	}, [fetchMessages]);

	const renderDate = (timestamp) => {
		if (!timestamp) return "";
		const d = new Date(timestamp);
		return d.toLocaleString("es-DO", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const messagesToRender = showAutogestion
		? messages
		: messages.filter((msg) => {
				return !msg.autogestion;
		  });

	const tags = chatData?.tags || [];

	const hasMultipleGroups = Array.isArray(chatData?.groups) && chatData.groups.length > 1;
	const groupLabel = hasMultipleGroups ? "Grupos" : "Grupo";
	let groupValue = "-";
	if (hasMultipleGroups) {
		groupValue = chatData.groups
			.map((g) => {
				return g.name || g;
			})
			.join(", ");
	} else if (Array.isArray(chatData?.groups) && chatData.groups.length === 1) {
		groupValue = chatData.groups[0].name || chatData.groups[0];
	} else {
		groupValue = chatData?.group?.name || chatData?.group || "-";
	}

	const renderFirstResponseHeader = () => {
		const refId = chatData?.refId || chatData?.id || "-";

		const getMinutesBetween = (start, end) => {
			if (!start || !end) return "-";
			const diffMs = new Date(end) - new Date(start);
			const mins = Math.floor(diffMs / 60000);
			return mins;
		};

		const formatMinutes = (totalMins) => {
			if (totalMins === "-") return "-";
			if (totalMins < 60) return `${totalMins} min`;

			const d = Math.floor(totalMins / (24 * 60));
			const h = Math.floor((totalMins % (24 * 60)) / 60);
			const m = totalMins % 60;

			const parts = [];
			if (d > 0) parts.push(`${d}d`);
			if (h > 0) parts.push(`${h}h`);
			if (m > 0 || (d === 0 && h === 0)) parts.push(`${m}m`);

			return parts.join(" ");
		};

		const totalResponseMinutes = getMinutesBetween(chatData?.createdDate, chatData?.closeDate);
		const formattedTotalTime = formatMinutes(totalResponseMinutes);

		return (
			<div className={styles.frHeaderWrapper}>
				<div className={styles.frHeaderTop}>
					<div className={styles.frHeaderActions}>
						<button type="button" className={styles.frHeaderBtn} onClick={handlePrint}>
							<Icon name="print" />
						</button>
						<button type="button" className={styles.frHeaderCloseBtn} onClick={onClose}>
							&times;
						</button>
					</div>
				</div>
				<div className={styles.frHeaderInfo}>
					<div className={styles.frInfoRow}>
						<span>
							Chat ID: <span className={styles.frDark}>{chatData?.chatId || chatData?.id || "-"}</span>
						</span>
						<span className={styles.frRefId}>
							Ref ID: <span className={styles.frDark}>{refId}</span>
						</span>
					</div>
					<div className={styles.frInfoRow}>
						<span>
							Cliente: <span className={styles.frBold}>{chatData?.contact?.name || "Cliente"}</span> •{" "}
							<span className={styles.frDark}>{chatData?.contact?.phone || "-"}</span>
						</span>
					</div>
					<div className={styles.frInfoRow}>
						<span>
							Agente Cierre: <span className={styles.frBold}>{chatData?.user?.fullName || chatData?.user?.name || "-"}</span>
						</span>
					</div>
					<div className={styles.frInfoRow}>
						<span>
							{groupLabel}: <span className={styles.frBold}>{groupValue}</span>
						</span>
					</div>
					{tags.length > 0 && (
						<div className={styles.frTagsRow}>
							{tags.map((tag, i) => {
								return (
									<span key={i} className={styles.frTag}>
										{tag.name || tag}
									</span>
								);
							})}
						</div>
					)}
				</div>

				<div className={styles.frStatsBox}>
					<div className={styles.frStatsGrid4}>
						<div className={styles.frStatColumn}>
							<div className={styles.frStatLabel}>F. ENTRADA</div>
							<div className={styles.frStatValueText}>{chatData?.createdDate ? renderDate(chatData.createdDate) : "-"}</div>
						</div>
						<div className={styles.frStatColumn}>
							<div className={styles.frStatLabel}>PRIMERA RESPUESTA</div>
							<div className={styles.frStatValueText}>{chatData?.firstResponseDate ? renderDate(chatData.firstResponseDate) : "-"}</div>
						</div>
						<div className={styles.frStatColumn}>
							<div className={styles.frStatLabel}>F. CIERRE</div>
							<div className={styles.frStatValueText}>{chatData?.closeDate ? renderDate(chatData.closeDate) : "-"}</div>
						</div>
						<div className={styles.frStatColumn}>
							<div className={styles.frStatLabel}>TIEMPO TOTAL</div>
							<div className={styles.frStatValue}>{formattedTotalTime}</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderDefaultHeader = () => {
		return (
			<>
				<button type="button" className={styles.cleanCloseBtn} onClick={onClose}>
					&times;
				</button>
				<div className={styles.headerWrapper}>
					<div className={styles.headerBox}>
						<div className={styles.headerLeft}>
							<div className={styles.avatar}>{chatData?.contact?.name ? chatData.contact.name.charAt(0).toUpperCase() : "C"}</div>
							<div className={styles.clientDetails}>
								<div className={styles.clientName}>{chatData?.contact?.name || "Cliente"}</div>
								<div className={styles.clientPhone}>{chatData?.contact?.phone || "-"}</div>
							</div>
						</div>
						<div className={styles.headerRight}>
							<div className={styles.infoGrid}>
								<div className={styles.infoItem}>
									<span className={styles.infoLabel}>CANAL</span>
									<span className={styles.infoValueBold}>{chatData?.channel?.label || "-"}</span>
								</div>
								<div className={styles.infoItem}>
									<span className={styles.infoLabel}>AGENTE</span>
									<span className={styles.infoValueBold}>{chatData?.user?.fullName || chatData?.user?.name || "-"}</span>
								</div>
								<div className={styles.infoItem}>
									<span className={styles.infoLabel}>CHAT-ID</span>
									<span className={styles.infoValueBold}>#{chatData?.chatId || chatData?.id || "-"}</span>
								</div>
							</div>
							<div className={styles.headerActions}>
								<Button icon="print" basic size="small" className={styles.printBtn} onClick={handlePrint} />
							</div>
						</div>
					</div>
				</div>
			</>
		);
	};

	return (
		<Modal open={open} onClose={onClose} className={styles.modal} closeOnDimmerClick={false}>
			{variant === "firstResponse" ? renderFirstResponseHeader() : renderDefaultHeader()}

			<Modal.Content className={styles.body}>
				<div ref={printRef} className={styles.printContainer}>
					<div className={styles.printOnlyHeader}>
						<div className={styles.printHeaderTitle}>
							<h1 className={styles.printHeaderMainTitle}>Reporte de Chat</h1>
							<h2 className={styles.printHeaderSubTitle}>{companyName}</h2>
						</div>
						<div className={styles.printHeaderFlex}>
							<div>
								<p>
									<strong>Chat ID:</strong> {chatData?.chatId || chatData?.id || "-"}
								</p>
								<p>
									<strong>Cliente:</strong> {chatData?.contact?.name || "-"} · {chatData?.contact?.phone || "-"}
								</p>
								<p>
									<strong>Agente Cierre:</strong> {chatData?.user?.fullName || chatData?.user?.name || "-"}
								</p>
								<p>
									<strong>{groupLabel}:</strong> {groupValue}
								</p>
								{tags.length > 0 && (
									<p>
										<strong>Etiquetas:</strong>{" "}
										{tags
											.map((tag) => {
												return tag.name || tag;
											})
											.join(", ")}
									</p>
								)}
							</div>
							<div className={styles.printHeaderRightText}>
								<p>
									<strong>Usuario:</strong> {userName}
								</p>
								<p>
									<strong>Fecha:</strong>{" "}
									{new Date().toLocaleString("es-DO", {
										day: "2-digit",
										month: "2-digit",
										year: "numeric",
										hour: "numeric",
										minute: "2-digit",
										hour12: true,
									})}
								</p>
							</div>
						</div>
						<hr />
					</div>
					{isLoading ? (
						<div className={styles.loaderContainer}>
							<Loader active inline="centered">
								Cargando mensajes...
							</Loader>
						</div>
					) : isEmpty(messagesToRender) ? (
						<div className={styles.emptyMessagesText}>No hay mensajes para este chat.</div>
					) : (
						messagesToRender.map((msg, index) => {
							const isClient = !msg.sentByAgent;
							const senderName = isClient ? chatData?.contact?.name || "Cliente" : msg.userName || "Agente";

							return (
								<div key={msg.id || index} className={`${styles.messageContainer} ${isClient ? styles.messageLeft : styles.messageRight}`}>
									<div className={`${styles.bubble} ${isClient ? styles.bubbleLeft : styles.bubbleRight}`}>
										<div className={`${styles.messageHeader} ${!isClient ? styles.messageHeaderRight : ""}`}>
											{isClient ? (
												<>
													<strong>{senderName}</strong>
													<span>{renderDate(msg.timestamp)}</span>
												</>
											) : (
												<>
													<span>{renderDate(msg.timestamp)}</span>
													<strong>{senderName}</strong>
												</>
											)}
										</div>
										<div className={styles.messageText}>{msg.text}</div>
									</div>
								</div>
							);
						})
					)}
				</div>
			</Modal.Content>

			<div className={styles.footer}>
				<div className={`${styles.footerInfo} ${styles.footerInfoWrapper}`}>
					<span>Mensajes: {messagesToRender.length}</span>
					<div className={styles.footerToggleWrapper}>
						<Checkbox
							toggle
							checked={showAutogestion}
							onChange={() => {
								return setShowAutogestion(!showAutogestion);
							}}
						/>
						<span
							role="button"
							tabIndex={0}
							className={styles.cursorPointer}
							onClick={() => {
								return setShowAutogestion(!showAutogestion);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									setShowAutogestion(!showAutogestion);
								}
							}}>
							Ver autogestión
						</span>
					</div>
				</div>
				<div>
					<Button className={styles.closeButton} onClick={onClose}>
						Cerrar
					</Button>
				</div>
			</div>
		</Modal>
	);
};

HistoricChatModal.propTypes = {
	dispatch: PropTypes.func.isRequired,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	chatData: PropTypes.object,
	companyName: PropTypes.string,
	userName: PropTypes.string,
	variant: PropTypes.string,
};

export default connect()(HistoricChatModal);
